import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { DASHBOARD_MODULE_MAP } from '#/modules/infra/dashboard/helper/dashboardModules';
import { readPeriod } from '#/modules/infra/dashboard/helper/dashboardRange';
import DashboardMetricCards from '#/modules/infra/dashboard/components/DashboardMetricCards';
import { formatCount } from '#/modules/infra/dashboard/helper/dashboardChartTheme';

/**
 * 單一分類（客戶／物料／生管）的狀態卡（上：目前待辦 `backlog`；下：完成期間 `completion`，期間＝頁面日期區間）：每種單據一張卡，一列兩欄，窄版換行；
 * `group.scrollX`（物料）改為單列不換行、橫向捲動，每塊最小 420px。
 * 只渲染 `data.modules` 內存在的 key（未授權 module 由後端省略）。
 */
const SUBHEADING_CLASS = 'text-xs font-medium text-muted-foreground';

const DashboardOverviewSection = ({ group, modules, range }) => {
  const configs = group.moduleKeys
    .map((key) => DASHBOARD_MODULE_MAP[key])
    .filter((config) => modules?.[config.key]);

  if (configs.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        group.scrollX
          ? 'flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:thin]'
          : 'grid grid-cols-1 gap-3 @3xl:grid-cols-2',
      )}
    >
      {configs.map((config) => {
        const moduleData = modules[config.key];
        const asOfDate = readPeriod(config.key, moduleData.period)?.asOfDate;
        return (
          <section
            key={config.key}
            className={cn(
              'flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-background p-3.5',
              group.scrollX && 'min-w-[420px] flex-1 shrink-0 snap-start',
            )}
          >
            <h2 className="font-semibold text-foreground">{config.title}</h2>
            <p className={SUBHEADING_CLASS}>目前待辦（不限日期）</p>
            <DashboardMetricCards
              config={config}
              cards={config.backlog}
              values={moduleData.backlog}
              kind="backlog"
              asOfDate={asOfDate}
            />
            {config.completion.length > 0 && moduleData.completion && (
              <>
                <p className={SUBHEADING_CLASS}>
                  完成期間 {range ? `${range.from}～${range.to}` : ''}
                </p>
                <DashboardMetricCards
                  config={config}
                  cards={config.completion}
                  values={moduleData.completion}
                  kind="completion"
                  range={range}
                  asOfDate={asOfDate}
                />
                {moduleData.completion.dateUnknown > 0 && (
                  <p className="text-xs text-muted-foreground">
                    另有 {formatCount(moduleData.completion.dateUnknown)} 筆已完成但完成日期不明，未計入。
                  </p>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};

DashboardOverviewSection.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    moduleKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    scrollX: PropTypes.bool,
  }).isRequired,
  modules: PropTypes.object,
  range: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
};

export default DashboardOverviewSection;
